// OpenRouter API Service
// Documentation: https://openrouter.ai/docs

import { validateEnvironment, EnvironmentConfig } from '../utils/envValidation';

interface OpenRouterMessage {
  role: 'user' | 'assistant' | 'system';
  content: string | Array<{
    type: 'text' | 'image_url';
    text?: string;
    image_url?: {
      url: string;
    };
  }>;
}

interface OpenRouterRequest {
  model: string;
  messages: OpenRouterMessage[];
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
}

interface OpenRouterResponse {
  id: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string | null;
      reasoning?: string | null;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

class OpenRouterService {
  private apiKey: string;
  private baseUrl: string = 'https://openrouter.ai/api/v1';
  private siteUrl: string;
  private siteName: string;
  private config: EnvironmentConfig;
  /** Abort a request if the model hasn't responded in time (ms). Free-tier
   *  models can queue/stall indefinitely, which otherwise freezes the UI. */
  private requestTimeoutMs: number = 45000;

  constructor(apiKey?: string, siteUrl?: string, siteName?: string) {
    // Validate environment and get configuration
    this.config = validateEnvironment();
    
    // Use provided parameters or fall back to environment variables
    this.apiKey = apiKey || this.config.openRouterApiKey;
    this.siteUrl = siteUrl || this.config.siteUrl;
    this.siteName = siteName || this.config.siteName;
    
    if (!this.apiKey) {
      const errorMessage = this.getDetailedErrorMessage();
      throw new Error(errorMessage);
    }
  }

  private getDetailedErrorMessage(): string {
    const isProd = process.env.NODE_ENV === 'production';
    
    if (isProd) {
      return `OpenRouter API key is not configured in production. 
      
To fix this:
1. Get your API key from: https://openrouter.ai/keys
2. Add REACT_APP_OPENROUTER_API_KEY to your production environment variables
3. Redeploy your application

For local development, create a .env file with:
REACT_APP_OPENROUTER_API_KEY=your_api_key_here`;
    } else {
      return `OpenRouter API key is required. 
      
To fix this:
1. Get your API key from: https://openrouter.ai/keys
2. Create a .env file in your project root with:
   REACT_APP_OPENROUTER_API_KEY=your_api_key_here
3. Restart your development server`;
    }
  }

  /**
   * Make a chat completion request to OpenRouter API
   */
  async chatCompletion(request: OpenRouterRequest): Promise<OpenRouterResponse> {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json',
      // Required by OpenRouter to enable free-model routing
      'HTTP-Referer': this.siteUrl || 'https://githubmate.ai/',
      'X-Title': this.siteName || 'GitHubMate',
    };

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.requestTimeoutMs);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: controller.signal
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error(`OpenRouter request timed out after ${this.requestTimeoutMs / 1000}s`);
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`OpenRouter API error: ${response.status} ${response.statusText}${body ? ` — ${body}` : ''}`);
    }

    return await response.json();
  }

  /**
   * Text-only chat completion
   */
  async textChat(model: string, messages: OpenRouterMessage[], options: Partial<OpenRouterRequest> = {}): Promise<string> {
    const request: OpenRouterRequest = {
      model,
      messages,
      temperature: 0.7,
      max_tokens: 1000,
      ...options
    };

    const response = await this.chatCompletion(request);
    const msg = response.choices[0]?.message;
    // Reasoning models (e.g. Nemotron, DeepSeek-R1) set content=null and put
    // the final answer in `reasoning` when they run out of tokens mid-think.
    // Fall back to reasoning so the UI always gets something useful.
    return msg?.content || msg?.reasoning || '';
  }

  /**
   * Vision chat with image analysis
   */
  async visionChat(
    model: string, 
    textPrompt: string, 
    imageUrl: string, 
    options: Partial<OpenRouterRequest> = {}
  ): Promise<string> {
    const messages: OpenRouterMessage[] = [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: textPrompt
          },
          {
            type: 'image_url',
            image_url: {
              url: imageUrl
            }
          }
        ]
      }
    ];

    const request: OpenRouterRequest = {
      model,
      messages,
      temperature: 0.7,
      max_tokens: 1000,
      ...options
    };

    const response = await this.chatCompletion(request);
    return response.choices[0]?.message?.content || '';
  }

  /**
   * Example: Analyze an image with text description
   */
  async analyzeImage(imageUrl: string, analysisPrompt: string = 'What is in this image?'): Promise<string> {
    return this.visionChat(
      'openrouter/free',
      analysisPrompt,
      imageUrl
    );
  }

  /**
   * Example: Generate code documentation
   */
  async generateDocumentation(codeContent: string, language: string = 'javascript'): Promise<string> {
    const prompt = `Please generate comprehensive documentation for the following ${language} code. Include function descriptions, parameters, return values, and usage examples:

${codeContent}`;

    return this.textChat(
      'openrouter/free',
      [{ role: 'user', content: prompt }],
      { temperature: 0.3, max_tokens: 2000 }
    );
  }

  /**
   * Example: Code review and suggestions
   */
  async reviewCode(codeContent: string, language: string = 'javascript'): Promise<string> {
    const prompt = `Please review the following ${language} code and provide:
1. Code quality assessment
2. Potential bugs or issues
3. Performance improvements
4. Security considerations
5. Best practices recommendations

Code:
${codeContent}`;

    return this.textChat(
      'openrouter/free',
      [{ role: 'user', content: prompt }],
      { temperature: 0.4, max_tokens: 2000 }
    );
  }

  /**
   * Example: Generate project architecture analysis
   */
  async analyzeArchitecture(projectStructure: string): Promise<string> {
    const prompt = `Based on the following project structure, please provide:
1. Architecture overview
2. Component relationships
3. Potential improvements
4. Scalability considerations
5. Technology stack analysis

Project Structure:
${projectStructure}`;

    return this.textChat(
      'openrouter/free',
      [{ role: 'user', content: prompt }],
      { temperature: 0.3, max_tokens: 2500 }
    );
  }

  /**
   * Get available models from OpenRouter
   */
  async getModels(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/models`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get usage statistics
   */
  async getUsage(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/auth/key`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch usage: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

// Export the service class
export default OpenRouterService;

// Export types for external use
export type {
  OpenRouterMessage,
  OpenRouterRequest,
  OpenRouterResponse
};

// Example usage:
/*
import OpenRouterService from './services/openRouterApi';
import { validateEnvironmentVariables } from '../utils/envValidation';

// Validate environment variables before using the service
validateEnvironmentVariables();

// Method 1: Using environment variables (recommended)
const openRouter = new OpenRouterService();

// Method 2: Passing parameters directly
const openRouter = new OpenRouterService(
  'your-api-key-here',
  'https://your-site.com',
  'Your Site Name'
);

// Text chat
const response = await openRouter.textChat(
  'openrouter/free',
  [{ role: 'user', content: 'Hello, how are you?' }]
);

// Vision chat with image
const imageAnalysis = await openRouter.analyzeImage(
  'https://example.com/image.jpg',
  'What objects do you see in this image?'
);

// Generate documentation
const documentation = await openRouter.generateDocumentation(codeString, 'typescript');

// Code review
const review = await openRouter.reviewCode(codeString, 'python');
*/ 